import React from 'react';
import { 
    TextField, 
    Button, 
    Checkbox, 
    Select, 
    MenuItem, 
    FormControl, 
    InputLabel, 
    FormControlLabel, 
    Grid, 
    Box, 
    Tooltip,
    Typography, 
    Paper,
    Card,
    CardContent,
    useTheme,
    useMediaQuery,
    IconButton,
    Collapse
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import WarningIcon from '@mui/icons-material/Warning';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import PhoneIcon from '@mui/icons-material/Phone';
import BluetoothIcon from '@mui/icons-material/Bluetooth';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DescriptionIcon from '@mui/icons-material/Description';
import ScheduleIcon from '@mui/icons-material/Schedule';
import GateOpeningTimes from '../../../../components/Settings/GateOpeningTimes';

// Reusable Setting Item Component with help tooltip
const SettingItem = ({ helpText, children }) => {
    const [showHelp, setShowHelp] = React.useState(false);
    
    return (
        <Box sx={{ 
            width: '100%',
            mb: { xs: 1, sm: 1.5 }
        }}>
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1
            }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {children}
                </Box>
                {helpText && (
                    <IconButton 
                        size="small" 
                        onClick={() => setShowHelp(!showHelp)}
                        sx={{ 
                            mt: 0.5,
                            color: 'action.active',
                            '&:hover': { color: 'primary.main' }
                        }}
                    >
                        <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>
            {helpText && (
                <Collapse in={showHelp}>
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            display: 'block',
                            color: 'text.secondary',
                            bgcolor: 'action.hover',
                            borderRadius: 1,
                            p: 1,
                            mt: 0.5,
                            lineHeight: 1.4
                        }}
                    >
                        {helpText}
                    </Typography>
                </Collapse>
            )}
        </Box>
    );
};

// Section Card Component
const SettingsSection = ({ title, icon: Icon, children }) => {
    return (
        <Card 
            elevation={0}
            sx={{ 
                mb: { xs: 2, sm: 3 },
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden'
            }}
        >
            <Box 
                sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1.5,
                    px: { xs: 2, sm: 2.5 },
                    py: { xs: 1.25, sm: 1.5 },
                    bgcolor: 'grey.50',
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                }}
            >
                {Icon && <Icon sx={{ fontSize: { xs: 18, sm: 20 }, color: 'primary.main' }} />}
                <Typography 
                    variant="subtitle2" 
                    sx={{ 
                        fontWeight: 600,
                        color: 'text.primary',
                        fontSize: { xs: '0.875rem', sm: '0.9rem' }
                    }}
                >
                    {title}
                </Typography>
            </Box>
            <CardContent sx={{ 
                px: { xs: 2, sm: 2.5 },
                py: { xs: 2, sm: 2 },
                '&:last-child': { pb: { xs: 2, sm: 2 } }
            }}>
                {children}
            </CardContent>
        </Card>
    );
};

// Responsive TextField Component
const ResponsiveTextField = ({ name, value, onChange, label, disabled, helpText, type = 'text' }) => {
    return (
        <SettingItem helpText={helpText}>
            <TextField
                fullWidth
                size="medium"
                label={label}
                name={name}
                value={value || ''}
                onChange={onChange}
                variant="outlined"
                disabled={disabled}
                type={type}
                sx={{
                    '& .MuiInputBase-input': {
                        fontSize: { xs: '1rem', sm: '0.95rem' },
                        py: { xs: 1.5, sm: 1.25 }
                    },
                    '& .MuiInputLabel-root': {
                        fontSize: { xs: '0.95rem', sm: '0.9rem' }
                    }
                }}
            />
        </SettingItem>
    );
};

// Responsive Checkbox Component
const ResponsiveCheckbox = ({ name, checked, onChange, label, disabled, helpText, warning }) => {
    return (
        <SettingItem helpText={helpText}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                    control={
                        <Checkbox 
                            name={name}
                            checked={checked || false}
                            onChange={onChange}
                            disabled={disabled}
                            sx={{
                                '& .MuiSvgIcon-root': {
                                    fontSize: { xs: 24, sm: 24 }
                                }
                            }}
                        />
                    }
                    label={
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                fontSize: { xs: '0.875rem', sm: '0.9rem' },
                                color: disabled ? 'text.disabled' : 'text.primary'
                            }}
                        >
                            {label}
                        </Typography>
                    }
                    sx={{
                        m: 0,
                        flex: 1,
                        '& .MuiFormControlLabel-label': {
                            flex: 1
                        }
                    }}
                />
                {warning && (
                    <Tooltip title={warning}>
                        <WarningIcon color='error' sx={{ ml: 1, fontSize: 20 }} />
                    </Tooltip>
                )}
            </Box>
        </SettingItem>
    );
};

const GateOpeningTab = ({ 
    settings, 
    handleInputChange,
    kiosks,
    disablePINAliases,
    masterPINAliases,
    masterTenantNameForPINs,
    gateSchedule,
    handleScheduleChange,
    gateTimesDialogOpen,
    setGateTimesDialogOpen,
    showBlueToothDialog
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
            {/* General Settings Section */}
            <SettingsSection title="General Settings" icon={SettingsIcon}>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <ResponsiveTextField
                            name="SettingGatePhoneNumber"
                            value={settings.SettingGatePhoneNumber}
                            onChange={handleInputChange}
                            label="Gate Phone Number"
                            helpText="Gate systems can be opened through voice call and/or text message. Enter the gate number including international dialling code."
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <ResponsiveTextField
                            name="SettingGateNomenclature"
                            value={settings.SettingGateNomenclature}
                            onChange={handleInputChange}
                            label="Gate Nomenclature"
                            helpText="The word used to describe the 'gate' (e.g. 'Gate', 'Door', 'Barrier') which appears in the kiosk and Connect app."
                        />
                    </Grid>
                </Grid>
            </SettingsSection>

            {/* Gate Opening Methods Section */}
            <SettingsSection title="Gate Opening Methods" icon={PhoneIcon}>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12} md={6}>
                        <ResponsiveCheckbox
                            name="SettingCallToOpenGate"
                            checked={settings.SettingCallToOpenGate}
                            onChange={handleInputChange}
                            label="Call gate phone number to open gate"
                            helpText="Makes a voice call to the gate number. Ensure gate accepts calls from +44 7360 269423"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ResponsiveCheckbox
                            name="SettingSendSMSToOpenGate"
                            checked={settings.SettingSendSMSToOpenGate}
                            onChange={handleInputChange}
                            label="Send SMS to open gate"
                            helpText="Sends an SMS message to the gate number. Ensure gate accepts texts from +44 7360 269423"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <ResponsiveTextField
                            name="SettingGateText"
                            value={settings.SettingGateText}
                            onChange={handleInputChange}
                            label="SMS gate text format"
                            disabled={!settings.SettingSendSMSToOpenGate}
                            helpText="The text message content sent to open the gate"
                        />
                    </Grid>
                </Grid>
            </SettingsSection>

            {/* Bluetooth Relay Section */}
            <SettingsSection title="Bluetooth Relay" icon={BluetoothIcon}>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12} sm={6} md={4}>
                        <ResponsiveCheckbox
                            name="SettingAllowBluetoothRelay"
                            checked={settings.SettingAllowBluetoothRelay}
                            onChange={handleInputChange}
                            label="Allow bluetooth relay"
                            helpText="Open gate via Bluetooth relay. Only available when using iOS/Android app for Kiosk UI."
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <ResponsiveTextField
                            name="SettingBluetoothOnMilliseconds"
                            value={settings.SettingBluetoothOnMilliseconds}
                            onChange={handleInputChange}
                            label="Relay on time (ms)"
                            disabled={!settings.SettingAllowBluetoothRelay}
                            helpText="Milliseconds to keep relay open. E.g. 100ms for gate, 10000ms for door."
                        />
                    </Grid>
                    {settings.SettingAllowBluetoothRelay && (
                        <Grid item xs={12} sm={6} md={4}>
                            <Box sx={{ pt: { xs: 0, sm: 0.5 } }}>
                                <Button 
                                    disabled={!settings.SettingAllowBluetoothRelay && settings.BluetoothPasscode}
                                    onClick={showBlueToothDialog} 
                                    variant="outlined"
                                    fullWidth={isMobile}
                                    sx={{ 
                                        py: { xs: 1.25, sm: 1 },
                                        fontSize: { xs: '0.9rem', sm: '0.875rem' }
                                    }}
                                >
                                    Reset Bluetooth
                                </Button>
                            </Box>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <SettingItem helpText="Choose another device configured to use Bluetooth to be signalled as well as (chain) or instead of (delegate) this one.">
                            <FormControl variant="outlined" fullWidth>
                                <InputLabel id="delegate-bluetooth-label">Delegate/chain bluetooth relay to another device</InputLabel>
                                <Select
                                    labelId="delegate-bluetooth-label"
                                    value={settings.SettingDelegateBluetoothRelayTenantId || 'none'}
                                    onChange={handleInputChange}
                                    label="Delegate/chain bluetooth relay to another device"
                                    name="SettingDelegateBluetoothRelayTenantId"
                                    sx={{
                                        '& .MuiSelect-select': {
                                            fontSize: { xs: '1rem', sm: '0.95rem' },
                                            py: { xs: 1.5, sm: 1.25 }
                                        }
                                    }}
                                >
                                    {kiosks.filter(kiosk => kiosk.SettingAllowBluetoothRelay || kiosk.SettingDelegateBluetoothRelayTenantId || kiosk.id === 'none').map((kiosk) => (
                                        <MenuItem key={kiosk.id} value={kiosk.id}>
                                            {kiosk.OrganisationName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </SettingItem>
                    </Grid>
                </Grid>
            </SettingsSection>

            {/* Kiosk Buttons Section */}
            <SettingsSection title="Kiosk Buttons" icon={TouchAppIcon}>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <ResponsiveCheckbox
                            name="SettingIncludePINSButton"
                            checked={settings.SettingIncludePINSButton}
                            onChange={handleInputChange}
                            label="Include PINs button"
                            helpText="Shows the PIN entry option (button) on the Kiosk"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <ResponsiveCheckbox
                            name="SettingAllowAliasesForPINs"
                            checked={masterPINAliases ?? (settings.SettingAllowAliasesForPINs || false)}
                            onChange={handleInputChange}
                            label="Enable aliases for PINs"
                            disabled={!settings.SettingIncludePINSButton || disablePINAliases}
                            helpText="PINs can be assigned to Aliases to notify specific people on successful PIN entry"
                            warning={disablePINAliases ? `This option is unavailable because PINs are shared from ${masterTenantNameForPINs}. Switch to ${masterTenantNameForPINs} to change this setting.` : null}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <ResponsiveCheckbox
                            name="SettingIncludeDeliveryButton"
                            checked={settings.SettingIncludeDeliveryButton}
                            onChange={handleInputChange}
                            label="Include Delivery button"
                            helpText="Shows the Delivery option (button) on the Kiosk"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <ResponsiveCheckbox
                            name="SettingSecondLevelDeliveryOption"
                            checked={settings.SettingSecondLevelDeliveryOption}
                            onChange={handleInputChange}
                            label="Second-level delivery menu"
                            disabled={!settings.SettingIncludeDeliveryButton}
                            helpText="Gives delivery users a choice to speak to owner or just request entry without speaking"
                        />
                    </Grid>
                </Grid>
            </SettingsSection>

            {/* Notifications Section */}
            <SettingsSection title="Notifications" icon={NotificationsIcon}>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12}>
                        <ResponsiveCheckbox
                            name="SettingCallToNotify"
                            checked={settings.SettingCallToNotify}
                            onChange={handleInputChange}
                            label="Call number to notify of visitor/delivery"
                            helpText="Triggers a call to the specified number, useful as a 'doorbell'"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <ResponsiveTextField
                            name="SettingNotifyNumber"
                            value={settings.SettingNotifyNumber}
                            onChange={handleInputChange}
                            label="Notify phone number"
                            disabled={!settings.SettingCallToNotify}
                            helpText="The phone number to call when notifying"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <ResponsiveTextField
                            name="SettingNotifyTimeoutSeconds"
                            value={settings.SettingNotifyTimeoutSeconds}
                            onChange={handleInputChange}
                            label="Notify timeout (seconds)"
                            disabled={!settings.SettingCallToNotify}
                            helpText="Seconds before cutting off. Short (6s) = couple of rings. Longer = keeps ringing until answered."
                        />
                    </Grid>
                </Grid>
            </SettingsSection>

            {/* Timeouts Section */}
            <SettingsSection title="Message Timeouts" icon={ScheduleIcon}>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <ResponsiveTextField
                            name="SettingDeliveryOrVisitorMessageTimeoutSeconds"
                            value={settings.SettingDeliveryOrVisitorMessageTimeoutSeconds}
                            onChange={handleInputChange}
                            label="Welcome message timeout (seconds)"
                            helpText="Seconds to display the welcome message after manual gate open before returning to main menu"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <ResponsiveTextField
                            name="SettingNonInteractiveWelcomeTimeoutSeconds"
                            value={settings.SettingNonInteractiveWelcomeTimeoutSeconds}
                            onChange={handleInputChange}
                            label="ANPR/PIN message timeout (seconds)"
                            helpText="Seconds to display welcome message from ANPR/PIN entry before returning to main menu"
                        />
                    </Grid>
                </Grid>
            </SettingsSection>

            {/* Entry Instructions Section */}
            <SettingsSection title="Entry Instructions" icon={DescriptionIcon}>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <ResponsiveCheckbox
                            name="SettingShowEntryInstructionsVisitors"
                            checked={settings.SettingShowEntryInstructionsVisitors}
                            onChange={handleInputChange}
                            label="Show entry instructions for visitors"
                            helpText="Display instructions to visitors after the responder lets them in"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <ResponsiveCheckbox
                            name="SettingShowEntryInstructionsDeliveries"
                            checked={settings.SettingShowEntryInstructionsDeliveries}
                            onChange={handleInputChange}
                            label="Show entry instructions for deliveries"
                            disabled={!settings.SettingIncludeDeliveryButton}
                            helpText="Display instructions to delivery personnel after entry is granted"
                        />
                    </Grid>

                    {settings.SettingShowEntryInstructionsVisitors && (
                        <Grid item xs={12}>
                            <Box sx={{ mb: 1 }}>
                                <Typography 
                                    variant="subtitle2" 
                                    sx={{ fontWeight: 600, mb: 0.5 }}
                                >
                                    Entry Instructions for Visitors
                                </Typography>
                                <Typography 
                                    variant="caption" 
                                    sx={{ color: 'text.secondary', display: 'block', mb: 1 }}
                                >
                                    This information appears to the user after the responder lets them in.
                                </Typography>
                            </Box>
                            <Paper 
                                elevation={0} 
                                sx={{ 
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    '& .ql-container': {
                                        fontSize: { xs: '0.95rem', sm: '1rem' }
                                    }
                                }}
                            >
                                <ReactQuill 
                                    value={settings.SettingEntryInstructionsVisitors || ''} 
                                    onChange={(content) => handleInputChange({target: {name: 'SettingEntryInstructionsVisitors', value: content}})} 
                                    modules={{
                                        clipboard: {
                                            matchVisual: false
                                        }
                                    }}
                                />
                            </Paper>
                        </Grid>
                    )}

                    {settings.SettingShowEntryInstructionsDeliveries && settings.SettingIncludeDeliveryButton && (
                        <Grid item xs={12}>
                            <Box sx={{ mb: 1 }}>
                                <Typography 
                                    variant="subtitle2" 
                                    sx={{ fontWeight: 600, mb: 0.5 }}
                                >
                                    Entry Instructions for Deliveries
                                </Typography>
                                <Typography 
                                    variant="caption" 
                                    sx={{ color: 'text.secondary', display: 'block', mb: 1 }}
                                >
                                    This information appears to the delivery person after they are let in.
                                </Typography>
                            </Box>
                            <Paper 
                                elevation={0} 
                                sx={{ 
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    '& .ql-container': {
                                        fontSize: { xs: '0.95rem', sm: '1rem' }
                                    }
                                }}
                            >
                                <ReactQuill 
                                    value={settings.SettingEntryInstructionsDeliveries || ''} 
                                    onChange={(content) => handleInputChange({target: {name: 'SettingEntryInstructionsDeliveries', value: content}})}
                                    modules={{
                                        clipboard: {
                                            matchVisual: false
                                        }
                                    }}
                                />
                            </Paper>
                        </Grid>
                    )}
                </Grid>
            </SettingsSection>

            {/* Time Restrictions Section */}
            <SettingsSection title="Time Restrictions" icon={ScheduleIcon}>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <ResponsiveCheckbox
                            name="SettingGateOpeningRestrictions"
                            checked={settings?.SettingGateOpeningRestrictions}
                            onChange={(e) => {
                                handleInputChange({
                                    target: {
                                        name: 'SettingGateOpeningRestrictions',
                                        value: e.target.checked
                                    }
                                });
                            }}
                            label="Enable gate opening day/time restrictions"
                            helpText="Restricts access via Number Plate, PIN or QR Code to specific days and times"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Box sx={{ pt: { xs: 0, sm: 0.5 } }}>
                            <Button 
                                variant="outlined" 
                                onClick={() => setGateTimesDialogOpen(true)}
                                disabled={!settings.SettingGateOpeningRestrictions}
                                fullWidth={isMobile}
                                sx={{ 
                                    py: { xs: 1.25, sm: 1 },
                                    fontSize: { xs: '0.9rem', sm: '0.875rem' }
                                }}
                            >
                                Configure Gate Opening Times
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </SettingsSection>

            <GateOpeningTimes
                open={gateTimesDialogOpen}
                onClose={() => setGateTimesDialogOpen(false)}
                settings={settings}
                gateSchedule={gateSchedule}
                handleInputChange={handleInputChange}
                handleScheduleChange={handleScheduleChange}
            />
        </Box>
    );
};

export default GateOpeningTab;
